import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../../abstract/api-error';
import { FileUploadService } from '../../../core/api-services/file-upload.service';
import { ChemicalsService } from '../../../core/data-services/chemicals.service';
import { SponsorsService } from '../../../core/data-services/sponsors.service';
import type { ChemicalLabel } from '../../../core/data-models/chemical-label.model';
import type { ChemicalCategory } from '../../../core/data-models/chemical-category.model';
import type { SignalWord } from '../../../core/data-models/signal-word.model';
import type { ChemicalCreateRequest } from '../../../core/request-models/chemical-create-request.model';
import type { ChemicalUpdateRequest } from '../../../core/request-models/chemical-update-request.model';
import { Sponsor } from '../../../core/data-models/sponsor.model';
import { SponsorCreateRequest } from '../../../core/request-models/sponsor-create-request.model';
import { environment } from '../../../../environments/environment';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';

declare var bootstrap: any;

/**
 * Chemical Form Component
 *
 * Handles both create and edit modes for ADN Recommended Chemicals.
 * Includes sponsor section with logo upload and archive functionality.
 */
@Component({
  selector: 'app-chemical-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective, PhoneFormatPipe],
  templateUrl: './chemical-form.component.html',
  styleUrl: './chemical-form.component.scss'
})
export class ChemicalFormComponent implements OnInit {
  chemicalId: string | null = null;
  isEditMode = false;
  loading = true;
  saving = false;
  errorMessage = '';
  successMessage = '';

  // Form
  form: FormGroup;

  // Sponsor section collapse state
  sponsorSectionExpanded = false;

  // Logo upload state
  logoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  uploadingLogo = false;

  // Archive modal state
  showArchiveModal = false;
  archiving = false;

  // Label management state
  labels: ChemicalLabel[] = [];
  showAddLabelForm = false;
  newLabelUrl = '';
  newLabelName = '';
  newLabelDate = '';
  newLabelSetCurrent = true;
  addingLabel = false;
  deletingLabelId: string | null = null;
  settingCurrentLabelId: string | null = null;

  // Tag arrays for target pests and crops
  targetPestsTags: string[] = [];
  cropsTags: string[] = [];
  newPestInput = '';
  newCropInput = '';

  // Sponsor selection
  sponsors: Sponsor[] = [];
  loadingSponsors = false;
  showNewSponsorModal = false;
  savingNewSponsor = false;
  newSponsorName = '';
  newSponsorWebsite = '';
  newSponsorPhone = '';
  newSponsorEmail = '';
  newSponsorError = '';

  // Category options
  categoryOptions: { value: ChemicalCategory; label: string }[] = [
    { value: 'herbicide', label: 'Herbicide' },
    { value: 'insecticide', label: 'Insecticide' },
    { value: 'fungicide', label: 'Fungicide' },
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'adjuvant', label: 'Adjuvant' },
    { value: 'igr', label: 'IGR (Insect Growth Regulator)' },
    { value: 'miticide', label: 'Miticide' },
    { value: 'pgr', label: 'PGR (Plant Growth Regulator)' },
    { value: 'other', label: 'Other' }
  ];

  // Signal word options
  signalWordOptions: { value: SignalWord; label: string }[] = [
    { value: 'CAUTION', label: 'Caution' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'DANGER', label: 'Danger' }
  ];

  // EPA Number validation pattern
  // Supports: XXX-XXX, XXXXX-XX, XXXXX-XXX, XXXXX-XXXX, XXXXX-XX-XXXXX
  private readonly epaNumberPattern = /^\d{3}-\d{3}$|^\d{5}-\d{1,4}$|^\d{5}-\d{1,4}-\d{5}$/;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private chemicalsService: ChemicalsService,
    private sponsorsService: SponsorsService,
    private fileUploadService: FileUploadService
  ) {
    this.form = this.fb.group({
      // Required fields
      name: ['', Validators.required],
      category: ['', Validators.required],
      signalWord: [''],  // Optional - may be populated from EPA enrichment

      // Optional fields
      epaNumber: [''],
      manufacturer: [''],
      activeIngredient: [''],
      formulation: [''],
      defaultRate: [''],
      rei: [''],
      phi: [''],
      compareTo: [''],
      rupFlag: [false],

      // Admin-only fields
      sdsUrl: [''],
      productUrl: [''],

      // Sponsor selection (linked to adn_sponsors table)
      sponsorId: [''],

      // Featured/Special Offer
      isFeatured: [false],
      specialOffer: ['']
    });
  }

  ngOnInit(): void {
    this.chemicalId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.chemicalId;

    // Load sponsors list
    this.loadSponsors();

    if (this.isEditMode) {
      this.loadChemical();
    } else {
      this.loading = false;
    }
  }

  /**
   * Load sponsors for dropdown
   */
  loadSponsors(): void {
    this.loadingSponsors = true;
    this.sponsorsService.listSponsors({ limit: 100 }).subscribe({
      next: (response) => {
        this.sponsors = response.items;
        this.loadingSponsors = false;
      },
      error: (error) => {
        console.error('Failed to load sponsors:', error);
        this.loadingSponsors = false;
      }
    });
  }

  /**
   * Load chemical data for edit mode
   */
  loadChemical(): void {
    this.loading = true;
    this.errorMessage = '';

    this.chemicalsService.getChemical(this.chemicalId!).subscribe({
      next: (chemical) => {
        if (!chemical) {
          this.errorMessage = 'Chemical not found';
          this.loading = false;
          return;
        }

        // Populate form
        this.form.patchValue({
          name: chemical.name ?? '',
          category: chemical.category || '',
          signalWord: chemical.signalWord || '',
          epaNumber: chemical.epaNumber || '',
          manufacturer: chemical.manufacturer || '',
          activeIngredient: chemical.activeIngredient || '',
          formulation: chemical.formulation || '',
          defaultRate: chemical.defaultRate || '',
          rei: chemical.rei || '',
          phi: chemical.phi || '',
          sponsorId: chemical.sponsorId || '',
          isFeatured: chemical.isFeatured || false,
          specialOffer: chemical.specialOffer || '',
          compareTo: chemical.compareTo || '',
          rupFlag: chemical.rupFlag || false,
          sdsUrl: chemical.sdsUrl || '',
          productUrl: chemical.productUrl || ''
        });

        // Set tag arrays
        this.targetPestsTags = chemical.targetPests || [];
        this.cropsTags = chemical.crops || [];

        // Set logo preview from selected sponsor
        const selectedSponsor = this.sponsors.find(s => s.id === chemical.sponsorId);
        if (selectedSponsor?.logoUrl) {
          this.logoPreviewUrl = selectedSponsor.logoUrl;
        }

        // Set labels
        this.labels = chemical.labels || [];

        // Expand sponsor section if sponsor is selected
        if (chemical.sponsorId) {
          this.sponsorSectionExpanded = true;
        }

        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load chemical:', error);
        this.errorMessage = 'Failed to load chemical';
        this.loading = false;
      }
    });
  }

  /**
   * Validate EPA number format
   */
  validateEpaNumber(): boolean {
    const epaNumber = this.form.get('epaNumber')?.value;
    if (!epaNumber) return true; // Optional field
    return this.epaNumberPattern.test(epaNumber);
  }

  /**
   * Get EPA number validation error
   */
  get epaNumberError(): string | null {
    const epaNumber = this.form.get('epaNumber')?.value;
    if (epaNumber && !this.validateEpaNumber()) {
      return 'Invalid EPA Number format';
    }
    return null;
  }

  /**
   * Toggle sponsor section
   */
  toggleSponsorSection(): void {
    this.sponsorSectionExpanded = !this.sponsorSectionExpanded;
  }

  /**
   * Handle logo file selection
   */
  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Only PNG and JPG files are allowed';
        return;
      }

      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than 2MB';
        return;
      }

      this.logoFile = file;

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreviewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Upload logo to S3 and get final URL
   */
  async uploadLogo(): Promise<string | null> {
    if (!this.logoFile) return null;

    this.uploadingLogo = true;

    try {
      // Pre-sign, then PUT the bytes straight to S3 (the hub signs logos public-read)
      const uploadData = await this.fileUploadService.getChemicalLogoUploadUrl(this.logoFile.name);
      await this.fileUploadService.uploadToSignedUrl(uploadData.uploadUrl, this.logoFile, {
        publicRead: true,
        contentType: uploadData.contentType,
      });

      this.uploadingLogo = false;
      return uploadData.fileUrl;
    } catch (error) {
      console.error('Failed to upload logo:', error);
      this.uploadingLogo = false;
      throw error;
    }
  }

  /**
   * Remove logo
   */
  removeLogo(): void {
    this.logoFile = null;
    this.logoPreviewUrl = null;
  }

  /**
   * Get the currently selected sponsor
   */
  get selectedSponsor(): Sponsor | null {
    const sponsorId = this.form.get('sponsorId')?.value;
    if (!sponsorId) return null;
    return this.sponsors.find(s => s.id === sponsorId) || null;
  }

  /**
   * Handle sponsor selection change
   */
  onSponsorChange(): void {
    const sponsor = this.selectedSponsor;
    if (sponsor?.logoUrl) {
      this.logoPreviewUrl = sponsor.logoUrl;
    } else {
      this.logoPreviewUrl = null;
    }
  }

  /**
   * Open the new sponsor modal
   */
  openNewSponsorModal(): void {
    this.newSponsorName = '';
    this.newSponsorWebsite = '';
    this.newSponsorPhone = '';
    this.newSponsorEmail = '';
    this.newSponsorError = '';
    this.showNewSponsorModal = true;

    setTimeout(() => {
      const modalElement = document.getElementById('newSponsorModal');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 0);
  }

  /**
   * Close the new sponsor modal
   */
  closeNewSponsorModal(): void {
    this.showNewSponsorModal = false;
    const modalElement = document.getElementById('newSponsorModal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  /**
   * Save a new sponsor and select it
   */
  async saveNewSponsor(): Promise<void> {
    if (!this.newSponsorName.trim()) {
      this.newSponsorError = 'Sponsor name is required';
      return;
    }

    this.savingNewSponsor = true;
    this.newSponsorError = '';

    try {
      const sponsorData: SponsorCreateRequest = {
        name: this.newSponsorName.trim(),
        website: this.newSponsorWebsite.trim() || null,
        phone: this.newSponsorPhone.trim() || null,
        email: this.newSponsorEmail.trim() || null
      };

      const newSponsor = await firstValueFrom(this.sponsorsService.createSponsor(sponsorData));

      if (newSponsor) {
        // Add to sponsors list and select it
        this.sponsors = [newSponsor, ...this.sponsors];
        this.form.patchValue({ sponsorId: newSponsor.id });
        this.onSponsorChange();
      }

      this.closeNewSponsorModal();
    } catch (error: any) {
      console.error('Failed to create sponsor:', error);
      this.newSponsorError = ApiError.fromHttpError(error).errors[0] ?? 'Failed to create sponsor';
    } finally {
      this.savingNewSponsor = false;
    }
  }

  /**
   * Clear selected sponsor
   */
  clearSponsor(): void {
    this.form.patchValue({ sponsorId: '' });
    this.logoPreviewUrl = null;
  }

  /**
   * Resolve a logo path to a full CDN URL.
   * Handles both relative paths and legacy full URLs.
   */
  getLogoFullUrl(logoUrl: string | null | undefined): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${environment.appCDN}/${logoUrl}`;
  }

  /**
   * Parse comma-separated string to array
   */
  private parseTagInput(input: string): string[] {
    return input
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  /**
   * Add a new pest tag
   */
  addPestTag(): void {
    const value = this.newPestInput.trim();
    if (value && !this.targetPestsTags.includes(value)) {
      this.targetPestsTags.push(value);
      this.newPestInput = '';
    }
  }

  /**
   * Remove a pest tag
   */
  removePestTag(index: number): void {
    this.targetPestsTags.splice(index, 1);
  }

  /**
   * Handle pest input keydown (Enter to add)
   */
  onPestInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addPestTag();
    }
  }

  /**
   * Add a new crop tag
   */
  addCropTag(): void {
    const value = this.newCropInput.trim();
    if (value && !this.cropsTags.includes(value)) {
      this.cropsTags.push(value);
      this.newCropInput = '';
    }
  }

  /**
   * Remove a crop tag
   */
  removeCropTag(index: number): void {
    this.cropsTags.splice(index, 1);
  }

  /**
   * Handle crop input keydown (Enter to add)
   */
  onCropInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addCropTag();
    }
  }

  /**
   * Clear all pest tags
   */
  clearAllPests(): void {
    this.targetPestsTags = [];
  }

  /**
   * Clear all crop tags
   */
  clearAllCrops(): void {
    this.cropsTags = [];
  }

  /**
   * Check if form can be saved
   */
  get canSave(): boolean {
    return this.form.valid && this.validateEpaNumber() && !this.saving;
  }

  /**
   * Save chemical (create or update)
   */
  async save(): Promise<void> {
    if (!this.canSave) return;

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formValue = this.form.getRawValue();

      // Get selected sponsor for flat field population (backwards compatibility)
      const sponsor = this.selectedSponsor;

      // Build request data
      const chemicalData: ChemicalCreateRequest | ChemicalUpdateRequest = {
        name: formValue.name,
        category: formValue.category,
        signalWord: formValue.signalWord || null,
        epaNumber: formValue.epaNumber || null,
        manufacturer: formValue.manufacturer || null,
        activeIngredient: formValue.activeIngredient || null,
        formulation: formValue.formulation || null,
        defaultRate: formValue.defaultRate || null,
        rei: formValue.rei || null,
        phi: formValue.phi || null,
        targetPests: this.targetPestsTags.length > 0 ? this.targetPestsTags : null,
        crops: this.cropsTags.length > 0 ? this.cropsTags : null,
        // Sponsor: use sponsorId and populate flat fields from selected sponsor
        sponsorId: formValue.sponsorId || null,
        sponsorName: sponsor?.name || null,
        sponsorLogoUrl: sponsor?.logoUrl || null,
        sponsorWebsite: sponsor?.website || null,
        sponsorPhone: sponsor?.phone || null,
        sponsorEmail: sponsor?.email || null,
        isFeatured: formValue.isFeatured || false,
        specialOffer: formValue.specialOffer || null,
        compareTo: formValue.compareTo || null,
        rupFlag: formValue.rupFlag || false,
        sdsUrl: formValue.sdsUrl || null,
        productUrl: formValue.productUrl || null
      };

      if (this.isEditMode) {
        await firstValueFrom(this.chemicalsService.updateChemical(this.chemicalId!, chemicalData));
      } else {
        await firstValueFrom(this.chemicalsService.createChemical(chemicalData as ChemicalCreateRequest));
      }

      this.successMessage = this.isEditMode ? 'Chemical updated successfully' : 'Chemical created successfully';

      // Navigate back to list after short delay
      setTimeout(() => {
        this.router.navigate(['/chemicals']);
      }, 1000);
    } catch (error: any) {
      console.error('Failed to save chemical:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to save chemical';
    } finally {
      this.saving = false;
    }
  }

  /**
   * Open archive confirmation modal
   */
  openArchiveModal(): void {
    this.showArchiveModal = true;
    setTimeout(() => {
      const modalElement = document.getElementById('archiveModal');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 0);
  }

  /**
   * Cancel archive
   */
  cancelArchive(): void {
    this.showArchiveModal = false;
    const modalElement = document.getElementById('archiveModal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  /**
   * Execute archive
   */
  async executeArchive(): Promise<void> {
    if (!this.chemicalId) return;

    this.archiving = true;
    this.errorMessage = '';

    try {
      await firstValueFrom(this.chemicalsService.archiveChemical(this.chemicalId));
      this.cancelArchive();
      this.successMessage = 'Chemical archived successfully';

      // Navigate back to list after short delay
      setTimeout(() => {
        this.router.navigate(['/chemicals']);
      }, 1000);
    } catch (error: any) {
      console.error('Failed to archive chemical:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to archive chemical';
    } finally {
      this.archiving = false;
    }
  }

  // ============================================
  // LABEL MANAGEMENT
  // ============================================

  /**
   * Toggle add label form
   */
  toggleAddLabelForm(): void {
    this.showAddLabelForm = !this.showAddLabelForm;
    if (!this.showAddLabelForm) {
      this.resetLabelForm();
    }
  }

  /**
   * Reset label form
   */
  private resetLabelForm(): void {
    this.newLabelUrl = '';
    this.newLabelName = '';
    this.newLabelDate = '';
    this.newLabelSetCurrent = true;
  }

  /**
   * Add a label
   */
  addLabel(): void {
    if (this.addingLabel || !this.chemicalId || !this.newLabelUrl.trim()) return;

    this.addingLabel = true;
    this.errorMessage = '';

    this.chemicalsService.addLabel(this.chemicalId, {
      labelUrl: this.newLabelUrl.trim(),
      labelName: this.newLabelName.trim() || undefined,
      acceptedDate: this.newLabelDate || undefined,
      setAsCurrent: this.newLabelSetCurrent
    }).subscribe({
      next: (label) => {
        if (label) {
          this.labels.push(label);
        }
        this.successMessage = 'Label added successfully';
        this.addingLabel = false;
        this.showAddLabelForm = false;
        this.resetLabelForm();
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to add label:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to add label';
        this.addingLabel = false;
      }
    });
  }

  /**
   * Delete a label
   */
  deleteLabel(label: ChemicalLabel): void {
    if (!this.chemicalId) return;

    if (!confirm('Are you sure you want to delete this label?')) {
      return;
    }

    this.deletingLabelId = label.id;

    this.chemicalsService.deleteLabel(this.chemicalId, label.id).subscribe({
      next: () => {
        this.labels = this.labels.filter(l => l.id !== label.id);
        this.successMessage = 'Label deleted';
        this.deletingLabelId = null;
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to delete label:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to delete label';
        this.deletingLabelId = null;
      }
    });
  }

  /**
   * Toggle a label's current status
   */
  setCurrentLabel(label: ChemicalLabel): void {
    if (!this.chemicalId) return;

    this.settingCurrentLabelId = label.id;

    this.chemicalsService.setCurrentLabel(this.chemicalId, label.id).subscribe({
      next: (result) => {
        // Replace labels array with fresh data from server
        this.labels = result.labels;
        this.successMessage = label.isCurrent ? 'Label unmarked as current' : 'Label set as current';
        this.settingCurrentLabelId = null;
        this.clearSuccessMessage();
      },
      error: (error) => {
        console.error('Failed to toggle current label:', error);
        this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to toggle current label';
        this.settingCurrentLabelId = null;
      }
    });
  }

  /**
   * Get the current label (or most recent)
   */
  getCurrentLabel(): ChemicalLabel | null {
    if (this.labels.length === 0) return null;
    const current = this.labels.find(l => l.isCurrent);
    if (current) return current;

    const sorted = [...this.labels].sort((a, b) => {
      if (!a.acceptedDate) return 1;
      if (!b.acceptedDate) return -1;
      return new Date(b.acceptedDate).getTime() - new Date(a.acceptedDate).getTime();
    });
    return sorted[0] || null;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | Date | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Clear success message after delay
   */
  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  /**
   * Navigate back to list
   */
  goBack(): void {
    this.router.navigate(['/chemicals']);
  }
}
